import React, { useCallback, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import ReviewCard from "../../../components/common/formkit/ReviewCard";
import SaaSNotFoundState from "../../../components/common/saas/SaaSNotFoundState";
import { getErrorText } from "../services/partnerNetworkService";
import { useListRecord } from "../hooks/useNetworkRecords";
import { fullName, formatDate, isActiveRecord } from "../utils/people";
import PersonAvatar from "./PersonAvatar";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import "../../../components/common/formkit/formkit.css";
import "../partnerNetwork.css";

const detailRows = (person) => [
  { label: "First name", value: person.first_name },
  { label: "Last name", value: person.last_name },
  { label: "Mobile number", value: person.mobile_number },
  { label: "Email address", value: person.email_address },
  { label: "Company", value: person.company },
  { label: "Specialization", value: person.specialization },
  { label: "Client type", value: person.client_type },
  { label: "Budget", value: person.budget },
  { label: "Address", value: person.address },
  { label: "Street", value: person.street },
  { label: "Apartment", value: person.apartment },
  { label: "Area", value: person.area_name },
  { label: "City", value: person.city_name },
  { label: "State", value: person.state_name },
  { label: "Country", value: person.country_name },
  { label: "Zip code", value: person.zip_code },
  { label: "Added on", value: formatDate(person.created_on) },
  { label: "Notes", value: person.notes },
];

/**
 * Read-only detail page shared by Partners and Clients. There is no get-by-id endpoint, so the record is read
 * from the list (see useListRecord).
 */
const PersonDetailPage = ({ config, load, remove, permissions }) => {
  const { noun, plural, home, canEditRecords } = config;
  const { Id } = useParams();
  const navigate = useNavigate();
  const seed = useLocation().state?.record;
  const { record, loading, error, notFound, refetch } = useListRecord(load, Id, seed, `Unable to load this ${noun.toLowerCase()}.`);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const closeDialog = useCallback(() => setConfirmDelete(false), []);

  const handleDelete = async () => {
    setBusy(true);
    try {
      await remove(Id);
      showToast(`${noun} deleted successfully`, { type: "success" });
      navigate(home, { replace: true });
    } catch (err) {
      showToast(getErrorText(err, `Unable to delete ${noun.toLowerCase()}`), { type: "error" });
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <div className="fk-page" style={{ maxWidth: 860 }} aria-busy="true" aria-label={`Loading ${noun.toLowerCase()}`}>
        <div className="fk-skel" style={{ height: 28, width: "40%", marginBottom: 16 }} />
        <div className="fk-skel" style={{ height: 220 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fk-page" style={{ maxWidth: 860 }}>
        <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
          <span>{error}</span>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={refetch}>
              Retry
            </button>
            <Link to={home} className="btn btn-sm btn-outline-secondary">
              Back to {plural}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !record) {
    return (
      <SaaSNotFoundState
        title={`${noun} not found`}
        description={`This ${noun.toLowerCase()} could not be found. It may have been removed.`}
        backLink={home}
        backLabel={`Back to ${plural}`}
      />
    );
  }

  const active = isActiveRecord(record);

  return (
    <div className="fk-page" style={{ maxWidth: 860 }}>
      <Notification />
      <div className="fk-header d-flex flex-wrap justify-content-between align-items-start gap-3">
        <div className="d-flex align-items-center gap-3">
          <PersonAvatar person={record} size={56} />
          <div>
            <h1 className="fk-title">{fullName(record)}</h1>
            <p className="fk-subtitle">{[record.mobile_number, record.email_address].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
        <div className="d-flex gap-2">
          {canEditRecords && permissions.canEdit && active && (
            <Link to={`${home}/${Id}/edit`} state={{ record }} className="btn btn-outline-primary d-inline-flex align-items-center gap-2">
              <FiEdit2 size={15} /> Edit
            </Link>
          )}
          {permissions.canDelete && active && (
            <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-2" onClick={() => setConfirmDelete(true)}>
              <FiTrash2 size={15} /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="fk-review-list">
        <ReviewCard title={`${noun} details`} rows={detailRows(record).filter((row) => row.value)} />
      </div>

      {confirmDelete && (
        <DeleteConfirmDialog
          title={`Delete ${noun.toLowerCase()}?`}
          message={`"${fullName(record)}" will be removed from your ${plural.toLowerCase()}.`}
          busy={busy}
          onCancel={closeDialog}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
};

export default PersonDetailPage;
